import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NotificationsBell from "./NotificationsBell";
import { Hexagon, LogOut, Sparkles, User as UserIcon, Users, BookOpen, LayoutDashboard, Menu, X, Heart } from "lucide-react";

export default function Header({ onOpenAI }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = () => { logout(); setMobileOpen(false); nav("/"); };

  const navItems = [
    { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
    { to: "/subjects",  label: "Subjects",  Icon: BookOpen },
    { to: "/people",    label: "People",    Icon: Users },
    { to: "/profile",   label: "Profile",   Icon: UserIcon },
    { to: "/donate",    label: "Donate",    Icon: Heart },
  ];

  const desktopLink = ({ to, label, Icon }) => {
    const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
    return (
      <Link
        key={to}
        to={to}
        data-testid={`nav-${label.toLowerCase()}`}
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
          active ? "text-[color:var(--bee-text)]" : "text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)]"
        }`}
      >
        <Icon size={16} strokeWidth={2} /> {label}
      </Link>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-[#FDFBF7]/95 backdrop-blur-xl border-b border-[color:var(--bee-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <Link to={user ? "/dashboard" : "/"} data-testid="brand-logo" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center">
              <Hexagon size={18} strokeWidth={2.5} className="text-[color:var(--bee-text)]" />
            </div>
            <span className="font-display text-2xl font-bold tracking-tight">FocusBee</span>
          </Link>

          {user ? (
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(desktopLink)}
            </nav>
          ) : null}

          <div className="flex items-center gap-1 sm:gap-2">
            {user ? (
              <>
                <button
                  onClick={onOpenAI}
                  data-testid="open-ai-assistant"
                  className="hidden sm:flex items-center gap-2 bee-btn-primary text-sm"
                >
                  <Sparkles size={16} /> Ask BeeAI
                </button>
                <NotificationsBell />
                <div className="hidden xl:flex items-center gap-2 pl-3 border-l border-[color:var(--bee-border)]">
                  <div
                    className="w-9 h-9 hex-clip flex items-center justify-center text-sm font-bold text-white"
                    style={{ background: user.avatar_color }}
                  >{user.name?.charAt(0)?.toUpperCase()}</div>
                  <span className="text-sm font-medium max-w-[8rem] truncate">{user.name}</span>
                </div>
                <button
                  onClick={doLogout}
                  data-testid="logout-button"
                  className="hidden lg:block p-2 text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)]"
                  aria-label="Logout"
                ><LogOut size={18} /></button>
                {/* Mobile hamburger */}
                <button
                  onClick={() => setMobileOpen(true)}
                  data-testid="mobile-menu-open"
                  className="lg:hidden p-2 text-[color:var(--bee-text)]"
                  aria-label="Menu"
                ><Menu size={22} /></button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login" className="bee-btn-secondary text-sm">Sign In</Link>
                <Link to="/register" data-testid="header-register" className="bee-btn-primary text-sm">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {user && mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-backdrop" />
          <aside data-testid="mobile-menu-panel" className="fixed right-0 top-0 h-full w-72 sm:w-80 bg-[color:var(--bee-bg)] z-50 shadow-2xl border-l border-[color:var(--bee-border)] flex flex-col lg:hidden animate-fade-up">
            <div className="p-4 border-b border-[color:var(--bee-border)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 hex-clip flex items-center justify-center text-sm font-bold text-white" style={{ background: user.avatar_color }}>
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{user.name}</div>
                  <div className="text-[10px] text-[color:var(--bee-muted)] truncate">{user.email}</div>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="p-1"><X size={20} /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1">
              {navItems.map(({ to, label, Icon }) => {
                const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${label.toLowerCase()}`}
                    className={`flex items-center gap-3 px-4 py-3 font-medium ${
                      active ? "bg-[color:var(--bee-primary)] text-[color:var(--bee-text)]" : "text-[color:var(--bee-text)] hover:bg-[color:var(--bee-surface-alt)]"
                    }`}
                  >
                    <Icon size={18} /> {label}
                  </Link>
                );
              })}
              <button
                onClick={() => { setMobileOpen(false); onOpenAI(); }}
                data-testid="mobile-open-ai"
                className="w-full flex items-center gap-3 px-4 py-3 font-medium bg-[color:var(--bee-text)] text-white mt-3"
              >
                <Sparkles size={18} /> Ask BeeAI
              </button>
            </nav>
            <div className="p-3 border-t border-[color:var(--bee-border)]">
              <button onClick={doLogout} data-testid="mobile-logout" className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
